import { useState } from 'react'
import { AlertTriangle, FileSpreadsheet, Plus, Trash2, X } from 'lucide-react'
import { SURVEY_QUESTION_TYPE } from '../../domain/domainCodes'
import Button from '../../components/Button'
import ExcelImportModal from '../../components/ExcelImportModal'
import { FormCols, SwitchField } from '../../components/Form'
import FormRow from '../../components/FormRow'
import IconButton from '../../components/IconButton'
import Select from '../../components/Select'
import { EmptyState } from '../../components/States'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { serverText } from '../../lib/i18n'
import { SURVEY_QUESTIONS_TEMPLATE_URL, importSurveyQuestions } from './engagementService'
import { surveysStrings as S } from './strings'
import './SurveyQuestionsEditor.css'

/** The block every caller reads — one source, whichever screen opens the editor. */
const D = S.form.questions

/** `SurveyQuestionType`: Radio 1, CheckBox 2, YesNo 3, Stars 4, ShortText 5, LongText 6.
    Only the first two are answered from a list the author writes. */
const CHOICE_TYPES = [SURVEY_QUESTION_TYPE.RADIO, SURVEY_QUESTION_TYPE.CHECKBOX]

/* A choice question opens with the two options it must at least have, so the author
   is filling in blanks rather than working out that there are blanks to add. */
const emptyOption = (index) => ({ id: -(index + 1), textAr: '', textEn: '' })
const TWO_OPTIONS = [emptyOption(0), emptyOption(1)]

const EMPTY_QUESTION = {
  textAr: '',
  textEn: '',
  type: SURVEY_QUESTION_TYPE.RADIO,
  mandatory: false,
  answers: TWO_OPTIONS,
}

const isChoice = (question) => CHOICE_TYPES.includes(Number(question.type))
const isCompleteOption = (option) => Boolean(option.textAr.trim() && option.textEn.trim())

/**
 * A question that `SaveSurveyValidator` would refuse is not offered for adding: both
 * texts, and for a choice question at least two options written in both languages —
 * the server's floor, since a single option is not a choice.
 */
const isCompleteQuestion = (question) =>
  Boolean(question.textAr.trim() && question.textEn.trim()) &&
  (!isChoice(question) || (question.answers ?? []).filter(isCompleteOption).length >= 2)

/**
 * The survey's questions — the legacy modal's third step: an "add question" form over
 * the list it feeds, and the "upload questions file" sheet beside it. Shared by the
 * surveys screen and the programme page, which open the same modal in the legacy and
 * post the same `SaveSurveyCommand`; the questions travel with the survey, so this is
 * state the parent form owns and this edits.
 *
 * The options of a choice question are a LIST — one row per option, Arabic beside
 * English, add and remove — as the legacy draws them. They were a single "a | b | c"
 * field, which nobody could be expected to guess and which wrote the Arabic text into
 * the English column.
 *
 * @param {{
 *   questions: object[],
 *   onChange: (questions: object[]) => void,
 *   error?: string,    shown under the list — "at least one question" at save time
 * }} props
 */
export default function SurveyQuestionsEditor({ questions, onChange, error }) {
  const toast = useToast()
  const [draft, setDraft] = useState(EMPTY_QUESTION)
  const [importing, setImporting] = useState(false)
  /* The rows a sheet import refused, kept on screen until dismissed: a toast is gone
     before anyone can read which rows and why. */
  const [skipped, setSkipped] = useState([])

  const setDraftField = (key) => (value) => setDraft((previous) => ({ ...previous, [key]: value }))
  const setOption = (index, key, value) =>
    setDraft((previous) => ({
      ...previous,
      answers: previous.answers.map((option, i) => (i === index ? { ...option, [key]: value } : option)),
    }))

  /* Negative ids until saved — the convention the rest of the feature uses for a row
     the server has not seen yet. */
  const nextId = (rows) => -(rows.filter((row) => row.id < 0).length + 1)

  const addQuestion = () => {
    if (!isCompleteQuestion(draft)) return
    const question = {
      ...draft,
      id: nextId(questions),
      /* A yes/no or a star rating writes its own answers; nothing typed under a
         previous type choice travels with it. */
      answers: isChoice(draft) ? draft.answers.filter(isCompleteOption) : [],
    }
    onChange([...questions, question])
    setDraft(EMPTY_QUESTION)
  }

  const importFile = async (file) => {
    const outcome = await importSurveyQuestions(file)
    if (!outcome.ok) return toast.error(serverText(outcome.message) || S.fail.importQuestions)
    setImporting(false)
    setSkipped(outcome.skipped)
    if (outcome.questions.length === 0) return toast.error(D.importedNone)
    /* Appended after what is already there, numbered past it. */
    let id = nextId(questions)
    onChange([...questions, ...outcome.questions.map((question) => ({ ...question, id: id-- }))])
    toast.success(D.imported(outcome.questions.length))
  }

  const answersOf = (question) => (isChoice(question) ? (question.answers ?? []) : [])
  const typeOptions = Object.entries(D.questionTypes).map(([value, label]) => ({ value, label }))

  return (
    <>
      <div className="sqe__toolbar">
        <Button type="button" variant="soft" onClick={() => setImporting(true)}>
          <FileSpreadsheet size={16} strokeWidth={1.75} aria-hidden="true" />
          {D.import}
        </Button>
      </div>

      {skipped.length > 0 && (
        <div className="sqe__skipped" role="status">
          <div className="sqe__skipped-head">
            <AlertTriangle size={16} strokeWidth={1.75} aria-hidden="true" />
            <strong>
              <bdi>{D.skippedTitle(skipped.length)}</bdi>
            </strong>
            <IconButton icon={X} label={D.dismiss} onClick={() => setSkipped([])} />
          </div>
          <ul className="sqe__skipped-list">
            {skipped.map((row) => (
              <li key={row.row}>
                {/* Row, dash, text as three flex items: a dash drawn by CSS between a
                    number and Latin text was re-ordered by the bidi algorithm. */}
                <span className="sqe__skipped-row">
                  <bdi>{D.skippedRow(row.row)}</bdi>
                  {row.text && (
                    <>
                      <span aria-hidden="true">—</span>
                      <bdi className="sqe__skipped-text">{row.text}</bdi>
                    </>
                  )}
                </span>
                {/* The reasons come from the server in English, as the sheet's own
                    column names are. `bdi` keeps them readable without pulling the
                    line to the far edge of an Arabic panel. */}
                <span className="sqe__skipped-why">
                  <bdi>{row.reasons.join('; ')}</bdi>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="sqe__adder">
        <FormCols>
          <FormRow label={D.questionAr}>
            {(id) => <TextField id={id} value={draft.textAr} onChange={setDraftField('textAr')} />}
          </FormRow>
          <FormRow label={D.questionEn}>
            {(id) => (
              <TextField id={id} dir="ltr" value={draft.textEn} onChange={setDraftField('textEn')} />
            )}
          </FormRow>
          <div className="sqe__type">
            <FormRow label={D.questionType}>
              {(id) => (
                <Select
                  id={id}
                  value={String(draft.type)}
                  onChange={(value) => setDraftField('type')(Number(value))}
                  options={typeOptions}
                />
              )}
            </FormRow>
            {/* What the respondent will be asked to do — in words, under the choice,
                because "single" and "multiple" alone told nobody what changes. */}
            <p className="sqe__type-help">
              <bdi>{D.typeHelp[String(draft.type)]}</bdi>
            </p>
          </div>
          <SwitchField
            checked={draft.mandatory}
            onChange={setDraftField('mandatory')}
            label={D.mandatory}
            hint={D.mandatoryHint}
          />
        </FormCols>

        {isChoice(draft) && (
          <fieldset className="sqe__options">
            <legend className="sqe__options-legend">
              <bdi>{D.options}</bdi>
              <span className="sqe__options-hint">
                <bdi>{D.optionsHint}</bdi>
              </span>
            </legend>
            <ol className="sqe__options-list">
              {draft.answers.map((option, index) => (
                <li key={option.id} className="sqe__option">
                  <span className="sqe__option-index tabular" aria-hidden="true">
                    {index + 1}
                  </span>
                  <TextField
                    value={option.textAr}
                    placeholder={D.optionAr}
                    aria-label={`${D.optionAr} ${index + 1}`}
                    onChange={(value) => setOption(index, 'textAr', value)}
                  />
                  <TextField
                    dir="ltr"
                    value={option.textEn}
                    placeholder={D.optionEn}
                    aria-label={`${D.optionEn} ${index + 1}`}
                    onChange={(value) => setOption(index, 'textEn', value)}
                  />
                  <IconButton
                    icon={Trash2}
                    label={D.removeOption}
                    tone="danger"
                    /* Two is the floor; the last two rows stay, blank or not. */
                    disabled={draft.answers.length <= 2}
                    onClick={() =>
                      setDraftField('answers')(draft.answers.filter((_, i) => i !== index))
                    }
                  />
                </li>
              ))}
            </ol>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                setDraftField('answers')([...draft.answers, emptyOption(draft.answers.length)])
              }
            >
              <Plus size={14} strokeWidth={2} aria-hidden="true" />
              {D.addOption}
            </Button>
          </fieldset>
        )}

        <Button type="button" onClick={addQuestion} disabled={!isCompleteQuestion(draft)}>
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
          {D.addQuestion}
        </Button>
      </div>

      {questions.length === 0 ? (
        <EmptyState title={D.noQuestions} />
      ) : (
        <ol className="sqe__list">
          {questions.map((question, index) => (
            <li key={question.id ?? index} className="sqe__row">
              <span className="sqe__index tabular">{index + 1}</span>
              <span className="sqe__texts">
                <bdi>{question.textAr}</bdi>
                <bdi className="sqe__en" dir="ltr">
                  {question.textEn}
                </bdi>
                <span className="sqe__meta">
                  <bdi>{D.questionTypes[String(question.type)]}</bdi>
                  {question.mandatory && <bdi> · {D.mandatory}</bdi>}
                  {answersOf(question).length > 0 && (
                    <bdi>
                      {' · '}
                      {answersOf(question)
                        .map((answer) => answer.textAr)
                        .join('، ')}
                    </bdi>
                  )}
                </span>
              </span>
              <IconButton
                icon={Trash2}
                label={D.removeQuestion}
                tone="danger"
                onClick={() => onChange(questions.filter((item) => item !== question))}
              />
            </li>
          ))}
        </ol>
      )}

      {error && (
        <p className="sqe__error" role="alert">
          {error}
        </p>
      )}

      <ExcelImportModal
        open={importing}
        title={D.importTitle}
        accept=".xlsx,.csv"
        templateHref={SURVEY_QUESTIONS_TEMPLATE_URL}
        strings={{ notes: D.importNotes }}
        onClose={() => setImporting(false)}
        onSubmit={importFile}
      />
    </>
  )
}
